import jsPDF from 'jspdf';
import 'jspdf-autotable';

export const generateTeacherResultsPDF = (teacher, t, language = 'en') => {
  // Create PDF document with appropriate text direction
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    putOnlyUsedFonts: true
  });

  // Add Arabic font support if needed
  if (language === 'ar') {
    doc.addFont('https://cdn.jsdelivr.net/npm/amiri-font@1.0.0/amiri-regular.ttf', 'Amiri', 'normal');
    doc.setFont('Amiri');
    doc.setR2L(true);
  }

  // Header
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('Minecraft Education', 105, 30, { align: 'center' });
  doc.text(t('title'), 105, 45, { align: 'center' });

  // Teacher Info
  doc.setFontSize(14);
  doc.setFont(undefined, 'normal');
  const leftEdge = language === 'ar' ? 160 : 20;
  const align = language === 'ar' ? 'right' : 'left';
  doc.text(`${t('registrationNumber')}: ${teacher.reg_no}`, leftEdge, 70, { align });
  doc.text(`${t('fullName')}: ${teacher.full_name}`, leftEdge, 85, { align });
  if (teacher.school) doc.text(`${t('school')}: ${teacher.school}`, leftEdge, 100, { align });

  // Add governorate if available
  let yPos = 100;
  if (teacher.governorate) {
    yPos += 15;
    const governorateName = t(`governorates.${teacher.governorate}`);
    doc.text(`${t('governorate')}: ${governorateName}`, leftEdge, yPos, { align });
  }

  if (teacher.city) {
    yPos += 15;
    doc.text(`${t('city')}: ${teacher.city}`, leftEdge, yPos, { align });
  }

  // Total Score
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text(`${t('totalScore')}: ${teacher.total_pct}/100`, leftEdge, yPos + 25, { align });

  // Assignments status
  if (teacher.assignments_completed === false) {
    doc.setTextColor(204, 85, 0); // Orange color
    doc.text(
      language === 'ar' 
        ? "الواجبات: غير مكتملة" 
        : "Assignments: Not completed", 
      leftEdge, yPos + 40, { align }
    );
    doc.setTextColor(0, 0, 0); // Reset to black
  }

  // Category Scores Table
  const categoryData = [
    [t('originality'), `${teacher.originality}/5`],
    [t('relevance'), `${teacher.relevance}/5`],
    [t('skills'), `${teacher.skills}/5`],
    [t('presentation'), `${teacher.presentation}/5`],
    [t('educationalValue'), `${teacher.educational_value}/5`]
  ];

  const tableY = teacher.assignments_completed === false ? yPos + 50 : yPos + 40;

  doc.autoTable({
    startY: tableY,
    head: [[t('categoryScores'), 'Score']],
    body: categoryData,
    theme: 'striped',
    styles: {
      font: language === 'ar' ? 'Amiri' : undefined,
      direction: language === 'ar' ? 'rtl' : 'ltr'
    }
  });

  // Status
  const statusY = doc.lastAutoTable.finalY + 20;
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  let statusColor;
  let statusText;

  switch (teacher.status_code) {
    case 'TOP_DISTINGUISHED':
      statusColor = [0, 170, 0];
      statusText = t('topDistinguished');
      break;
    case 'PASSED':
      statusColor = [85, 85, 255];
      statusText = t('passed');
      break;
    case 'FAIL':
      statusColor = [255, 170, 0];
      statusText = t('didNotPass');
      break;
  }

  doc.setTextColor(...statusColor);
  doc.text(`${language === 'ar' ? 'الحالة' : 'Status'}: ${statusText}`, leftEdge, statusY, { align });

  // Status Message
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(12);
  
  // Special message for incomplete assignments
  let statusMessage;
  if (teacher.assignments_completed === false) {
    statusMessage = language === 'ar' 
      ? "عزيزي المعلم، لم تقدّم جميع الواجبات المطلوبة لاجتياز هذا التدريب."
      : "Dear Teacher, you did not provide all the assignments required to pass this training.";
    doc.setTextColor(204, 85, 0); // Orange color
  } else {
    statusMessage = t(`statusMessages.${teacher.status_code}`);
  }
  
  const textWidth = language === 'ar' ? 170 : 170;
  const splitMessage = doc.splitTextToSize(statusMessage, textWidth);
  doc.text(splitMessage, language === 'ar' ? 190 : 20, statusY + 15, { align });
  
  // Reset color to black
  doc.setTextColor(0, 0, 0);

  // Status Note
  const noteY = statusY + 15 + (splitMessage.length * 7);
  const statusNote = t(`statusNotes.${teacher.status_code}`);
  const splitNote = doc.splitTextToSize(statusNote, textWidth);
  doc.text(splitNote, language === 'ar' ? 190 : 20, noteY, { align });

  // Registration Link
  const linkY = noteY + (splitNote.length * 7) + 10;
  doc.setTextColor(0, 0, 255);
  const registrationText = language === 'ar' 
    ? "رابط التسجيل: https://forms.office.com/r/eQaC1bkC59" 
    : "Registration Link: https://forms.office.com/r/eQaC1bkC59";
  doc.text(registrationText, language === 'ar' ? 190 : 20, linkY, { align });

  // Footer
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  const footerText = language === 'ar' 
    ? `تم إنشاؤه بتاريخ: ${new Date().toLocaleDateString('ar-SA')}` 
    : `Generated on: ${new Date().toLocaleDateString()}`;
  doc.text(footerText, language === 'ar' ? 190 : 20, 280, { align });

  return doc;
};

export const generateTeachersListPDF = (teachers, t, language = 'en') => {
  // Create PDF document with appropriate text direction
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
    putOnlyUsedFonts: true
  });

  // Add Arabic font support if needed
  if (language === 'ar') {
    doc.addFont('https://cdn.jsdelivr.net/npm/amiri-font@1.0.0/amiri-regular.ttf', 'Amiri', 'normal');
    doc.setFont('Amiri');
    doc.setR2L(true);
  }

  // Header
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('Minecraft Education - ' + t('teacherRecords'), 150, 20, { align: 'center' });

  // Table data
  const headers = [
    language === 'ar' ? 'رقم التسجيل' : 'Reg No',
    language === 'ar' ? 'الاسم' : 'Name',
    language === 'ar' ? 'المدرسة' : 'School',
    language === 'ar' ? 'المحافظة' : 'Governorate',
    language === 'ar' ? 'النتيجة' : 'Score',
    language === 'ar' ? 'الواجبات' : 'Assignments',
    language === 'ar' ? 'الحالة' : 'Status'
  ];

  const tableData = teachers.map(teacher => [
    teacher.reg_no,
    teacher.full_name,
    teacher.school || '-',
    teacher.governorate ? t(`governorates.${teacher.governorate}`) : '-',
    `${teacher.total_pct}%`,
    teacher.assignments_completed !== false ? 
      (language === 'ar' ? 'مكتمل' : 'Completed') : 
      (language === 'ar' ? 'غير مكتمل' : 'Incomplete'),
    t(
      teacher.status_code === 'TOP_DISTINGUISHED'
        ? 'topDistinguished'
        : teacher.status_code === 'PASSED'
        ? 'passed'
        : 'didNotPass'
    )
  ]);

  doc.autoTable({
    startY: 30,
    head: [headers],
    body: tableData,
    theme: 'striped',
    styles: {
      font: language === 'ar' ? 'Amiri' : undefined,
      direction: language === 'ar' ? 'rtl' : 'ltr'
    }
  });

  // Footer
  const footerText = language === 'ar' 
    ? `تم إنشاؤه بتاريخ: ${new Date().toLocaleDateString('ar-SA')}` 
    : `Generated on: ${new Date().toLocaleDateString()}`;
  doc.setFontSize(10);
  doc.text(footerText, language === 'ar' ? 280 : 20, 200);

  return doc;
};